/**
 * Server Progress Tracking Service
 * Sends quiz progress events to the Worker API for analytics
 *
 * Tracking by mode:
 * - Production: Track all events
 * - Test: Track all events (marked as testMode: true)
 * - In Progress: Skip tracking (just previewing draft)
 */

import { createLogger } from '../core/logger';
import {
  getFunnelName,
  getProgressApiUrl,
  isInProgressMode,
  isTestMode
} from '../core/config';
import { getState } from '../core/state';
import {
  ProgressEventType,
  ProgressEvent,
  QuestionAnsweredData,
  QuizCompletedData,
  SmartPathShownData,
  SmartPathActionData,
  LeadSubmittedData
} from '../types';

const logger = createLogger('Progress');

// Queue for batching events (reduces API calls)
let eventQueue: ProgressEvent[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;
const FLUSH_DELAY = 1000; // Batch events within 1 second

/**
 * Get session ID from state
 */
function getSessionId(): string {
  return getState().session.sessionId;
}

/**
 * Get funnel ID from state
 */
function getFunnelId(): string {
  return getState().funnel?.funnelID || getFunnelName() || 'unknown';
}

/**
 * Create a progress event
 */
function createEvent(
  type: ProgressEventType,
  data?: ProgressEvent['data']
): ProgressEvent {
  return {
    type,
    sessionId: getSessionId(),
    funnelId: getFunnelId(),
    timestamp: Date.now(),
    testMode: isTestMode(),
    data
  };
}

/**
 * Queue an event for sending
 */
function queueEvent(event: ProgressEvent): void {
  // Skip tracking in In Progress mode (just previewing)
  if (isInProgressMode()) {
    logger.debug('Progress tracking skipped (In Progress mode)', { type: event.type });
    return;
  }

  eventQueue.push(event);
  logger.debug('Event queued', { type: event.type, queueLength: eventQueue.length });

  // Schedule flush if not already scheduled
  if (!flushTimeout) {
    flushTimeout = setTimeout(flushEvents, FLUSH_DELAY);
  }
}

/**
 * Flush queued events to server
 */
async function flushEvents(): Promise<void> {
  flushTimeout = null;

  if (eventQueue.length === 0) return;

  const events = [...eventQueue];
  eventQueue = [];

  const funnelName = getFunnelName();
  const url = getProgressApiUrl();

  logger.info('Sending progress events', { count: events.length, funnelName });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        funnelName,
        events
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    logger.debug('Progress events sent successfully');
  } catch (error) {
    logger.error('Failed to send progress events', error);
    // Re-queue failed events for retry (limit to prevent infinite growth)
    if (eventQueue.length < 50) {
      eventQueue.unshift(...events);
    }
  }
}

/**
 * Force flush any pending events (call on page unload)
 */
export function flushPendingEvents(): void {
  if (flushTimeout) {
    clearTimeout(flushTimeout);
    flushTimeout = null;
  }

  if (eventQueue.length > 0) {
    // Use sendBeacon for reliable delivery on page unload
    const funnelName = getFunnelName();
    const url = getProgressApiUrl();

    const payload = JSON.stringify({
      funnelName,
      events: eventQueue
    });

    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, payload);
      logger.debug('Progress events sent via sendBeacon', { count: eventQueue.length });
    }

    eventQueue = [];
  }
}

// ============================================
// Public tracking functions
// ============================================

/**
 * Track quiz started
 */
export function trackQuizStarted(): void {
  const event = createEvent('quiz_started');
  queueEvent(event);
}

/**
 * Track question answered
 */
export function trackQuestionAnswered(data: QuestionAnsweredData): void {
  const event = createEvent('question_answered', data);
  queueEvent(event);
}

/**
 * Track quiz completed
 */
export function trackQuizCompleted(data: QuizCompletedData): void {
  const event = createEvent('quiz_completed', data);
  // Flush immediately on completion - important event
  queueEvent(event);
  flushEvents();
}

/**
 * Track smart path shown
 */
export function trackSmartPathShown(data: SmartPathShownData): void {
  const event = createEvent('smart_path_shown', data);
  queueEvent(event);
}

/**
 * Track smart path action
 */
export function trackSmartPathAction(data: SmartPathActionData): void {
  const event = createEvent('smart_path_action', data);
  queueEvent(event);
}

/**
 * Track form started (user focused first field)
 */
export function trackFormStarted(): void {
  const event = createEvent('form_started');
  queueEvent(event);
}

/**
 * Track lead submitted
 */
export function trackLeadSubmitted(data: LeadSubmittedData): void {
  const event = createEvent('lead_submitted', data);
  // Flush immediately - important event
  queueEvent(event);
  flushEvents();
}

// ============================================
// Setup page unload handler
// ============================================

if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushPendingEvents();
    }
  });

  window.addEventListener('pagehide', flushPendingEvents);
}
